import React, { createContext, useContext, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { NavigationPage, PageNavigation, PageTarget } from "./navigation";

type Commands = {
  requestPage: (number: number) => boolean;
  recordCurrentPage: (number: number) => void;
  beginNavigation: () => (target: PageTarget) => void;
};
const NavigationContext = createContext<Commands | null>(null);
const CurrentPageContext = createContext<number>(1);

export function PageNavigationProvider({ pages, children }: {
  pages: NavigationPage[];
  children: React.ReactNode;
}) {
  const controller = useRef<PageNavigation | null>(null);
  const [currentPage, setCurrentPage] = useState(pages[0]?.pageNumber || 1);
  const location = useLocation();
  const latestPages = useRef(pages);
  useLayoutEffect(() => { latestPages.current = pages; }, [pages]);

  useLayoutEffect(() => {
    const root = document.getElementById("project-root");
    if (!root) return;
    const navigation = new PageNavigation(root, setCurrentPage);
    controller.current = navigation;
    navigation.setPages(latestPages.current);
    return () => {
      navigation.destroy();
      controller.current = null;
    };
  }, []);

  useLayoutEffect(() => { controller.current?.setPages(pages); }, [pages]);

  useLayoutEffect(() => {
    const number = Number(new URLSearchParams(location.search).get("displayedPageNumber"));
    const navigation = controller.current;
    if (navigation && !navigation.requestPage(number)) {
      navigation.requestPage(latestPages.current[0]?.pageNumber);
    }
    // A mode can change the gaps between identical paper sizes. Restore the URL
    // page once per route transition; passive replaceState never enters this effect.
  }, [location.key, location.pathname, location.search]);

  const commands = useMemo<Commands>(() => ({
    requestPage: number => controller.current?.requestPage(number) ?? false,
    recordCurrentPage: number => controller.current?.recordCurrentPage(number),
    beginNavigation: () => controller.current?.beginNavigation() ?? (() => {}),
  }), []);

  return (
    <NavigationContext.Provider value={commands}>
      <CurrentPageContext.Provider value={currentPage}>{children}</CurrentPageContext.Provider>
    </NavigationContext.Provider>
  );
}

export function usePageNavigation() {
  const navigation = useContext(NavigationContext);
  if (!navigation) throw new Error("Page navigation requires PageNavigationProvider");
  return navigation;
}

export const useCurrentPage = () => useContext(CurrentPageContext);
